import React from "react";
import dayjs from "dayjs";
import { formatString } from "utils";
import Modal from "components/Modal";

const CustomRequestDetailsModal = ({ isOpen, onClose, request }) => {
    if (!request) return null;

    const {
        orderId,
        horseName,
        status,
        saddleType,
        saddleSpecs,
        shippingDestination,
        shippingAddress,
        manufacturerName,
        customerNotes,
        customOrderStatus,
        createdAt,
    } = request;
    console.log({ customOrderStatus });

    const getStatusColor = (status) => {
        const s = status?.toLowerCase();
        if (s?.includes('pending')) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        if (s?.includes('success') || s?.includes('shipped') || s?.includes('paid') || s?.includes('fulfilled')) return 'bg-green-100 text-green-800 border-green-200';
        if (s?.includes('cancel') || s?.includes('fail')) return 'bg-red-100 text-red-800 border-red-200';
        return 'bg-blue-100 text-blue-800 border-blue-200';
    };

    return (
        <Modal
            isCloseIcon
            modalIsOpen={isOpen}
            closeModal={onClose}
            shouldCloseOnOverlayClick={true}
            className="w-[90%] sm:w-[600px] lg:w-[800px] max-h-[90vh] overflow-y-auto !p-0 !pt-8 !rounded-[32px]"
            title="Custom Request Details"
            titleClassName="!text-[32px] !font-barlowCondensed font-bold uppercase tracking-tight"
        >
            <div className="p-8 space-y-8 font-montserrat">
                <div className="flex justify-between items-start -mt-4 mb-2">
                    <p className="text-sm text-gray-400 font-medium font-montserrat tracking-tight">#{orderId}</p>
                </div>

                {/* Status and Basic Info */}
                <div className="flex flex-wrap justify-between items-start gap-4">
                    <div className="space-y-1">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-barlow">Status</p>
                        <span className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(status)}`}>
                            {formatString(status)}
                        </span>
                    </div>
                    <div className="space-y-1 text-right">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-barlow">Requested On</p>
                        <p className="text-[var(--color-primary)] font-semibold">{dayjs.unix(Number(createdAt)).format("MMMM D, YYYY h:mm A")}</p>
                    </div>
                </div>

                {/* Horse & Saddle Type */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 p-6 rounded-2xl border border-gray-100">
                    <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 font-barlow">Horse Name</p>
                        <p className="text-lg font-bold text-[var(--color-primary)]">{horseName}</p>
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 font-barlow">Saddle Type</p>
                        <p className="text-lg font-bold text-[var(--color-primary)]">{formatString(saddleType)}</p>
                    </div>
                    {manufacturerName && (
                        <div className="md:col-span-2 pt-4 border-t border-gray-200">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 font-barlow">Manufacturer</p>
                            <p className="text-lg font-bold text-[var(--color-primary)]">{manufacturerName}</p>
                        </div>
                    )}
                </div>

                {/* Order Timeline */}
                {customOrderStatus && customOrderStatus.length > 0 && (
                    <div className="space-y-6">
                        <div className="flex items-center gap-3">
                            <div className="w-1.5 h-8 bg-[var(--color-primary)] rounded-full"></div>
                            <h3 className="text-xl font-bold text-[var(--color-primary)] font-barlowCondensed uppercase tracking-tight">
                                Order Timeline
                            </h3>
                        </div>

                        <div className="relative pl-8 space-y-8">
                            {/* Vertical Line */}
                            <div className="absolute left-[8px] top-2 bottom-2 w-[2px] bg-gray-100"></div>

                            {[...customOrderStatus]
                                ?.sort((a, b) => {
                                    const getTime = (ts) => dayjs.unix(Number(ts))?.isValid() && String(ts)?.length <= 10 ? Number(ts) * 1000 : new Date(ts).getTime();
                                    return getTime(b.timestamp) - getTime(a.timestamp);
                                })
                                ?.filter((step, index, self) =>
                                    index === self.findIndex((s) => s.status === step.status)
                                )
                                ?.map((step, index) => (
                                    <div key={index} className="relative">
                                        {/* Dot */}
                                        <div className={`absolute -left-[32px] top-1.5 w-[18px] h-[18px] rounded-full border-[3px] border-white shadow-sm z-10 ${index === 0 ? 'bg-[#43BC8E]' : 'bg-[#5c7eb5]'
                                            }`}></div>

                                        <div className="flex flex-col">
                                            <p className="font-bold text-[var(--color-primary)] uppercase font-barlow tracking-wide leading-tight">
                                                {formatString(step.status)}
                                            </p>
                                            <p className="text-xs sm:text-sm text-gray-400 font-medium">
                                                {dayjs.unix(Number(step.timestamp)).isValid() && String(step.timestamp).length <= 10
                                                    ? dayjs.unix(Number(step.timestamp)).format("MMMM D, YYYY [at] h:mm A")
                                                    : dayjs(step.timestamp).format("MMMM D, YYYY [at] h:mm A")}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                )}

                {/* Specifications */}
                {saddleSpecs && (
                    <div className="space-y-4">
                        <h3 className="text-lg font-bold text-[var(--color-primary)] flex items-center gap-2 font-barlow uppercase tracking-tight">
                            <svg className="w-5 h-5 text-[var(--color-secondary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            Specifications
                        </h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-6 gap-x-4">
                            {Object.entries(saddleSpecs).map(([key, value]) => (
                                value && value !== 'NONE' && (
                                    <div key={key}>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 font-barlow">{formatString(key)}</p>
                                        <p className="text-sm font-semibold text-[var(--color-charcoal)] break-words">{formatString(value)}</p>
                                    </div>
                                )
                            ))}
                        </div>
                    </div>
                )}

                {/* Shipping Info */}
                <div className="space-y-4">
                    <h3 className="text-lg font-bold text-[var(--color-primary)] flex items-center gap-2 font-barlow uppercase tracking-tight">
                        <svg className="w-5 h-5 text-[var(--color-secondary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Shipping Information
                    </h3>
                    <div className="bg-[#F9FAFB] p-5 rounded-2xl border border-gray-100">
                        <p className="text-sm font-bold text-gray-500 mb-2">{shippingDestination}</p>
                        {shippingAddress && (
                            <div className="text-sm text-gray-600 space-y-1">
                                <p>{shippingAddress.street}</p>
                                <p>{shippingAddress.city}, {shippingAddress.state} {shippingAddress.zipCode}</p>
                                <p>{shippingAddress.country}</p>
                                {shippingAddress.phone && <p className="pt-2 font-medium text-[var(--color-primary)]">📞 {shippingAddress.phone}</p>}
                            </div>
                        )}
                    </div>
                </div>

                {/* Customer Notes */}
                {customerNotes && (
                    <div className="space-y-4 pb-4">
                        <h3 className="text-lg font-bold text-[var(--color-primary)] font-barlow uppercase tracking-tight">Customer Notes</h3>
                        <div className="bg-amber-50/50 p-5 rounded-2xl border border-amber-100/50 italic text-gray-700 leading-relaxed shadow-sm">
                            &quot;{customerNotes}&quot;
                        </div>
                    </div>
                )}


                <div className="pt-8 border-t border-gray-100 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-8 py-3 bg-[var(--color-primary)] text-white font-bold rounded-2xl hover:opacity-90 transition-all shadow-lg active:scale-95 font-montserrat"
                    >
                        Close
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default CustomRequestDetailsModal;
